'use client';

import { LinkWithChildren } from 'mode-shared/components/Header/types';
import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useMemo } from 'react';
import { ChevronIcon } from 'mode-shared/icons';
import { ExternalLinkIcon } from 'mode-shared/components/icons';
import Image from 'next/image';
import { twMerge } from 'tailwind-merge';
import { useSynthDiscordLink } from 'mode-shared/hooks/content';

export const Sidebar = ({
  links,
  children,
  logo,
  className,
}: {
  links: LinkWithChildren[];
  children?: React.ReactNode;
  logo: React.ReactNode;
  className?: string;
}) => {
  const currentRoute = usePathname();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const { data: discordLink } = useSynthDiscordLink();

  const navItems = useMemo(
    () =>
      links.map((link) => {
        if (link.href === '') {
          link.href = discordLink || '';
        }
        return link;
      }),
    [links, discordLink]
  );

  const toggleExpanded = (itemKey: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemKey)) {
      newExpanded.delete(itemKey);
    } else {
      newExpanded.add(itemKey);
    }
    setExpandedItems(newExpanded);
  };

  return (
    <aside
      className={twMerge(
        'sticky shrink-0 top-0 z-50 border-neutral-900 border-r h-screen w-[240px] bg-neutral-black px-4 py-6 overflow-y-auto',
        className
      )}
    >
      <div className="h-full flex flex-col">
        <div className="flex items-center mb-8">{logo}</div>
        <nav className="flex-1 space-y-1 hidden lg:block">
          {navItems.map(({ text, href, disabled, children, target, icon, showExternalIcon, hide }) => {
            if (hide) {
              return null;
            }

            if (children && children.length > 0) {
              const isExpanded = expandedItems.has(text);
              const hasActiveChild = children.some((child) => currentRoute?.includes(child.href));

              return (
                <div key={text} className="space-y-1">
                  <button
                    onClick={() => toggleExpanded(text)}
                    className={clsx(
                      'w-full flex items-center justify-between px-3 py-2 text-neutral-100 font-medium rounded-lg transition-colors hover:bg-neutral-900',
                      hasActiveChild && 'text-primary bg-primary/8',
                      disabled && 'pointer-events-none text-neutral-600'
                    )}
                  >
                    <span className="flex items-center gap-3">
                      {icon && <Image src={icon} alt={text} width={16} height={16} />}
                      {text}
                    </span>
                    <ChevronIcon
                      className={clsx(
                        'w-3 h-3 text-neutral-400 transition-transform duration-300 ease-in-out',
                        isExpanded && 'rotate-180'
                      )}
                    />
                  </button>

                  <div
                    className={clsx(
                      'ml-4 space-y-1 overflow-hidden transition-all duration-300 ease-in-out',
                      isExpanded ? 'max-h-screen' : 'max-h-0'
                    )}
                  >
                    {children.map((child) => {
                      if (child.hide) {
                        return null;
                      }

                      return (
                        <Link
                          key={child.href}
                          href={child.href}
                          target={child.target}
                          className={clsx(
                            'flex items-center gap-2 p-2 text-sm text-neutral-300 rounded-lg transition-colors hover:bg-neutral-900 hover:text-neutral-100',
                            currentRoute?.includes(child.href) && 'text-primary bg-primary/8',
                            child.disabled && 'pointer-events-none text-neutral-600'
                          )}
                        >
                          {child.icon && <Image src={child.icon} alt={child.text} width={16} height={16} />}
                          {child.text}
                          {child.showExternalIcon && <ExternalLinkIcon size={16} />}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={text}
                href={href}
                target={target}
                className={clsx(
                  disabled && 'pointer-events-none text-neutral-600',
                  currentRoute?.includes(href) && 'text-primary bg-primary/8',
                  'hover:bg-neutral-900 hover:text-neutral-100',
                  'flex items-center gap-2 px-3 py-2 text-neutral-100 font-medium rounded-lg transition-colors w-full'
                )}
              >
                {icon && <Image src={icon} alt={text} width={16} height={16} />}
                {text}
                {showExternalIcon && <ExternalLinkIcon size={16} />}
              </Link>
            );
          })}

          {children && <div className="px-3 py-2">{children}</div>}
        </nav>
      </div>
    </aside>
  );
};
